import { CandidatLink } from '../../components/candidat/CandidatLink';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Loader2, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthToken } from '../../lib/authToken';
import { GroupeTab } from './GroupeTab';
import { IndividuelTab } from './IndividuelTab';
import { PeriodeFilter, SESSION_DEBUT, SESSION_FIN } from './PeriodeFilter';
import { BilansTab } from './BilansTab';
import { ExportPdfDialog } from './ExportPdfDialog';
import { ObservationsPeriodePanel } from './ObservationsPeriodePanel';
import { saveBlob } from '../../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RapportDossierPanel({ dossierId, initialVue = 'rapport' }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);
  const [periode, setPeriode] = useState('session');
  const [vue, setVue] = useState(initialVue);
  const [debut, setDebut] = useState(SESSION_DEBUT);
  const [fin, setFin] = useState(SESSION_FIN);
  const [candidatId, setCandidatId] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [candidatData, setCandidatData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [pdfDialog, setPdfDialog] = useState(false);

  const fetchGroup = useCallback(async () => {
    if (!dossierId || !debut || !fin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/rapports/dossier/${dossierId}`, { headers, params: { debut, fin } });
      setGroupData(res.data);
    } catch {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  }, [dossierId, debut, fin, headers]);

  const fetchCandidat = useCallback(async () => {
    if (!dossierId || !candidatId || !debut || !fin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/rapports/candidat/${candidatId}`, { headers, params: { dossier_id: dossierId, debut, fin } });
      setCandidatData(res.data);
    } catch {
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  }, [dossierId, candidatId, debut, fin, headers]);

  useEffect(() => { if (!candidatId) fetchGroup(); }, [candidatId, fetchGroup]);
  useEffect(() => { if (candidatId) fetchCandidat(); }, [candidatId, fetchCandidat]);

  const handleExport = async (fileFormat, options = {}) => {
    const base = candidatId
      ? `${API_URL}/rapports/candidat/${candidatId}`
      : `${API_URL}/rapports/dossier/${dossierId}`;
    const params = candidatId ? { dossier_id: dossierId, debut, fin } : { debut, fin, ...options };
    const url = fileFormat === 'pdf' ? `${base}/export-pdf` : `${base}/export`;
    setExporting(fileFormat);
    try {
      const res = await axios.get(url, { headers, params, responseType: 'blob' });
      const match = (res.headers['content-disposition'] || '').match(/filename="(.+)"/);
      saveBlob(res.data, match ? match[1] : `rapport.${fileFormat === 'pdf' ? 'pdf' : 'xlsx'}`);
      toast.success(fileFormat === 'pdf' ? 'Export PDF téléchargé' : 'Export Excel téléchargé');
      setPdfDialog(false);
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  };

  const vueToggle = (
    <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
      {[['rapport', 'Rapport'], ['bilan', 'Bilan de formation']].map(([v, l]) => (
        <button key={v} type="button" onClick={() => setVue(v)} className={`px-3 py-1.5 text-sm rounded-md ${vue === v ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-600 hover:text-slate-900'}`} data-testid={`vue-${v}-btn`}>{l}</button>
      ))}
    </div>
  );
  if (vue === 'bilan') {
    return <div className="space-y-4" data-testid="rapport-dossier-panel">{vueToggle}<BilansTab dossierId={dossierId} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="rapport-dossier-panel">
      {vueToggle}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <PeriodeFilter periode={periode} setPeriode={setPeriode} debut={debut} setDebut={setDebut} fin={fin} setFin={setFin} />
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={() => handleExport('xlsx')} disabled={!!exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-excel-btn">
            {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />}
            Excel
          </Button>
          <Button type="button" size="sm" onClick={() => (candidatId ? handleExport('pdf') : setPdfDialog(true))} disabled={!!exporting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="export-pdf-btn">
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
            PDF
          </Button>
        </div>
      </div>

      {candidatId ? (
        <div className="space-y-3">
          <Button type="button" variant="outline" size="sm" onClick={() => { setCandidatId(null); setCandidatData(null); }} data-testid="back-to-group-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Retour au groupe
          </Button>
          {candidatData && (
            <p className="text-sm font-semibold text-slate-700">
              Rapport individuel — <CandidatLink dossierId={dossierId} candidatId={candidatData.candidat.id} icon>{candidatData.candidat.nom} {candidatData.candidat.prenom}</CandidatLink>
            </p>
          )}
          <IndividuelTab data={candidatData} loading={loading} />
        </div>
      ) : (
        <>
          <GroupeTab data={groupData} loading={loading} onSelectCandidat={(cid) => setCandidatId(cid)} />
          {groupData && <ObservationsPeriodePanel dossierId={dossierId} debut={debut} fin={fin} data={groupData} onSaved={fetchGroup} />}
        </>
      )}
      {pdfDialog && <ExportPdfDialog open onOpenChange={setPdfDialog} candidats={groupData?.candidats || []} exporting={exporting === 'pdf'} onExport={(opts) => handleExport('pdf', { groupe: opts.groupe, sections: opts.sections.join(','), annexes: opts.annexes })} />}
    </div>
  );
}
